<ng-container *ngIf="!showSelectedPlan else planSelected;">
  <!-- Legacy Unlimited Minutes Warning Banner -->
  <div class="legacy-minutes-banner" *ngIf="isOnLegacyUnlimitedMinutes">
    <div class="legacy-minutes-banner-icon">
      <ng-icon [name]="'heroExclamationTriangle'" [size]="'24px'" [color]="'#B45309'"></ng-icon>
    </div>
    <p class="legacy-minutes-banner-text">
      <span class="legacy-minutes-banner-title">Heads up: You're currently on a legacy billing plan with unlimited
        minutes.</span>
      <br>
      <span class="legacy-minutes-banner-subtitle">If you change plans, you'll move to our new limited-minutes pricing
        and won't be able to switch back.</span>
    </p>
  </div>
  <!-- Shopify billing is monthly-only (EVERY_30_DAYS), so the annual toggle is
       hidden for Shopify merchants — they can never be charged annually. -->
  <div class="plan-selection-wrapper" [ngClass]="{'credit-card-added-version': hasCreditCardOnFile}"
    *ngIf="!hideIntervalToggle && !subscription.isShopifyBillingProvider">
    <div class="plan-option-selection-wrapper">
      <div class="plan-option-selection-interval" (click)="selectInterval(SubscriptionInterval.Monthly)"
        [ngClass]="{'selected': selectedInterval === SubscriptionInterval.Monthly}">Pay Monthly
      </div>
      <div class="plan-option-selection-interval" (click)="selectInterval(SubscriptionInterval.Yearly)"
        [ngClass]="{'selected': selectedInterval === SubscriptionInterval.Yearly}">Pay Annually
        <div class="plan-option-selection-interval-badge"
          [ngClass]="{'selected': selectedInterval === SubscriptionInterval.Yearly}">2 months free</div>
      </div>
    </div>
  </div>
  <div class="d-flex align-items-stretch plan-options justify-content-center"
    [ngClass]="{'credit-card-added-version': hasCreditCardOnFile, 'billing-layout': !isOnboardingLayout, 'recommended-layout': !!recommendedPlan}">
    <div *ngFor="let plan of planOptionsDisplay; trackBy: trackByPlan" class="plan-option-wrapper"
      [ngClass]="{'recommended': isRecommendedPlan(plan)}">
      <div class="popular-plan-header" *ngIf="!recommendedPlan" [ngClass]="{'invisible': plan.value !== 'Scale'}">
        <ng-icon [name]="'heroSparklesSolid'"></ng-icon>
        <span>Most Popular</span>
      </div>
      <!-- ENG-2292: full-width "Recommended for you" banner on the API-recommended plan -->
      <div class="recommended-plan-header" *ngIf="recommendedPlan && isRecommendedPlan(plan)">
        <ng-icon [name]="'heroSparklesSolid'"></ng-icon>
        <span>{{ 'GUIDED_SETUP.CHOOSE_PLAN.RECOMMENDED_FOR_YOU' | translate }}</span>
      </div>
      <div class=" plan-option {{plan.value?.toLowerCase()}}" [ngClass]="{'selected': isPlanSelected(plan), 
      'popular-plan': isHighlightedPlan(plan)}">
        <div>
          <div class="d-flex align-items-center justify-content-between plan-option-name-wrapper">
            <div class="d-flex align-items-center">
              <div class="plan-option-name">{{ plan.value }}</div>
            </div>
          </div>
          <div *ngIf="subscription.isShopifyBillingProvider && (subscription.isTrialing || !subscription.isActive)" class="free-trial-badge">{{ trialDaysLeft ? trialDaysLeft + '-day' : '7-day' }} free trial</div>
          <div class="price">
            <span class="price-amount d-flex align-items-center">
              <span *ngIf="plan?.isYearly" class="original-price leading">{{ plan?.fullMonthlyPrice }}</span>
              {{ plan?.price }}
              <span *ngIf="plan?.originalPrice && selectedInterval === SubscriptionInterval.Monthly"
                class="original-price">{{ plan?.originalPrice }}</span>
              <div *ngIf="plan?.isYearly" class="plan-option-selection-interval-badge wider">Save ${{ plan?.annualSavings }}</div>
            </span>
            <div class="billing-info">{{ intervalText }}<span *ngIf="plan?.isYearly">, ${{ plan?.annualBillingAmount
                }} billed annually</span>
            </div>
          </div>
          <div class="separator-gradient"></div>
          <!-- Shopify shows the API-sourced highlight tagline; other providers keep
               the existing translated plan description. -->
          <div class="plan-option-description" *ngIf="plan.highlight">{{ plan.highlight }}</div>
          <div class="plan-option-description" *ngIf="!plan.highlight"
            [innerHTML]="getPlanDescriptionTranslationKey(plan) | translate">
          </div>
          <!-- Curated commerce content (Shopify-billed, or Stripe billing + the
               Shopify app) supplies its own per-tier header from the API
               (Professional has none); generic plans keep the standard headers.
               Driven by content presence (plan.highlight), not billing provider. -->
          <ng-container *ngIf="plan.highlight; else standardPlanHeaders">
            <div class="feature-highlight" *ngIf="plan.inheritsLabel">{{ plan.inheritsLabel }}</div>
          </ng-container>
          <ng-template #standardPlanHeaders>
            <div class="feature-highlight" *ngIf="plan.value === 'Professional'">24/7 AI answering service that knows your business.</div>
            <div class="feature-highlight" *ngIf="plan.value === 'Scale'">Everything in Professional, plus:</div>
            <div class="feature-highlight" *ngIf="plan.value === 'Growth'">Everything in Scale, plus:</div>
          </ng-template>
          <div class="d-flex align-items-start feature" *ngFor="let feature of plan.features; trackBy: trackByFeature"
            [ngClass]="{'mb-0': (feature.isHiddenOnMobileCollapsed && !isExpanded && isMobileVersion) }">
            <ng-container *ngIf="!(feature.isHiddenOnMobileCollapsed && !isExpanded && isMobileVersion)">
              <ng-icon class="feature-check-icon" [name]="'heroCheckCircle'" [size]="'16px'" [color]="'#822AC6'"
                [strokeWidth]="'2'"></ng-icon>
              <span class="feature-description">{{ feature.text }}</span>
            </ng-container>
          </div>
        </div>
        <rosie-button class="select-plan-button-mobile" [customClass]="getCustomClassForPlan(plan)"
          (click)="handleSelectPlanClick(plan)" [inProgress]="isPlanChanging(plan)"
          [disabled]="isAnyPlanChanging" [translationKey]="getSelectPlanButtonTranslationKey(plan)"></rosie-button>
      </div>
    </div>
  </div>
  <!-- All plans include (Go Live page) -->
  <rosie-all-plans-include *ngIf="isOnboardingLayout && !subscription.isShopifyBillingProvider"></rosie-all-plans-include>
  <div *ngIf="allPlansHaveCoupon() && !hasCreditCardOnFile" class="coupon-preview-display">
    <div class="coupon-added-detailed-wrapper">
      <div class="coupon-added-detailed">
        <ng-icon [name]="'heroTagSolid'" [size]="'18px'" [color]="'#9332E0'"></ng-icon>
        {{ getAppliedCoupon()?.name || getAppliedCoupon()?.id }}
        <ng-icon [name]="'heroXMark'" [size]="'16px'" [color]="'#9332E0'" class="coupon-remove-icon"
          (click)="removeCoupon()"></ng-icon>
      </div>
      <span class="coupon-description">{{ getCouponDescription() }}</span>
    </div>
  </div>
</ng-container>
<ng-template #planSelected>
  <div class="plan-option-wrapper previewed-plan selected-plan" [ngClass]="{'with-addon': showWebsiteTextingAddon}">
    <div class="selected-plan-badge">
      <span>Selected Plan</span>
    </div>
    <div class=" plan-option {{selectedPlanDisplay?.value?.toLowerCase()}} selected pointer"
      [ngClass]="{'credit-card-added-version': hasCreditCardOnFile}">
      <div>
        <div class="d-flex align-items-center justify-content-between plan-option-name-wrapper">
          <div class="d-flex align-items-center">
            <div class="plan-option-name">{{ selectedPlanDisplay?.value }}</div>
          </div>
        </div>
        <div class="d-flex align-items-center justify-content-between">
          <div class="price">
            <span class="price-amount d-flex align-items-center" [shimmer]="isCouponRevalidating">
              <span *ngIf="selectedPlanDisplay?.isYearly" class="original-price leading">{{
                selectedPlanDisplay?.fullMonthlyPrice }}</span>
              {{ selectedPlanDisplay?.price }}
              <span *ngIf="selectedPlanDisplay?.originalPrice && selectedInterval === SubscriptionInterval.Monthly"
                class="original-price">{{
                selectedPlanDisplay?.originalPrice }}</span>
              <div class="plan-option-selection-interval-badge wider" *ngIf="selectedPlanDisplay?.isYearly">Save ${{
                selectedPlanDisplay?.annualSavings }}
              </div>
            </span>
            <div class="billing-info">{{ intervalText }}<span *ngIf="selectedPlanDisplay?.isYearly">, ${{
                selectedPlanDisplay?.annualBillingAmount
                }} billed annually</span>
            </div>
          </div>
          <div class="change-plan" (click)="changePlan()" *ngIf="isMobileVersion && !hideChangePlan">Change Plan</div>
        </div>
        <ng-container *ngIf="!isMobileVersion">
          <div class="features-content-wrapper">
            <div class="separator-gradient"></div>
            <div class="plan-option-description"
              [innerHTML]="getPlanDescriptionTranslationKey(selectedPlanDisplay) | translate">
            </div>
            <div class="feature-highlight" *ngIf="selectedPlanDisplay?.value === 'Professional'">24/7 AI answering
              service that knows your business.
            </div>
            <div class="feature-highlight" *ngIf="selectedPlanDisplay?.value === 'Scale'">Everything in Professional,
              plus:
            </div>
            <div class="feature-highlight" *ngIf="selectedPlanDisplay?.value === 'Growth'">Everything in Scale, plus:
            </div>
            <div class="d-flex align-items-start feature"
              *ngFor="let feature of selectedPlanDisplay?.features; trackBy: trackByFeature"
              [ngClass]="{'mb-0': (feature.isHiddenOnMobileCollapsed && !isExpanded && isMobileVersion) }">
              <ng-container *ngIf="!(feature.isHiddenOnMobileCollapsed && !isExpanded  && isMobileVersion)">
                <ng-icon class="feature-check-icon" [name]="'heroCheckCircle'" [size]="'16px'" [color]="'#822AC6'"
                  [strokeWidth]="'2'"></ng-icon>
                <span class="feature-description">{{ feature.text }}</span>
              </ng-container>
            </div>
          </div>
        </ng-container>
      </div>
      <div class="change-plan" (click)="changePlan()" *ngIf="!isMobileVersion && !hideChangePlan">Change Plan</div>
    </div>
  </div>

  <!-- Website Texting Add-on -->
  <div *ngIf="showWebsiteTextingAddon" class="wt-addon-tile">
    <div class="wt-addon-header">
      <span class="wt-addon-title">Website Texting</span>
      <span class="wt-addon-badge">ADD-ON</span>
    </div>
    <div class="wt-addon-body">
      <div class="wt-addon-price-wrapper">
        <span class="wt-addon-price">$50</span>
        <span class="wt-addon-interval d-block">per month + overages</span>
      </div>
      <rosie-switch [ariaLabel]="'Website Texting'" [control]="websiteTextingControl" (state)="onWebsiteTextingToggle()">
      </rosie-switch>
    </div>
  </div>
</ng-template>